import classNames from 'classnames'
import FieldsFactory from 'components/form/FieldsFactory'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback} from 'react'
import {IoChevronDown, IoChevronUp} from 'react-icons/io5'
import {useToggle} from 'react-use'
import {mapProps} from 'recompose'
import {NullAsDefault, renderOwnChild, renderSelf, Visible,} from 'views/Shared'

export const DefaultAdvancedToggle = ({
                                          className = 'w-full flex justify-end items-center py-1',
                                          title,
                                          onClick,
                                          expanded = false,
                                          renderTitle = renderSelf,
                                          prefixIcon,
                                          icons = [IoChevronDown, IoChevronUp],
                                      }) => {
    const t = useTranslate()
    const Component = expanded ? icons[1] : icons[0]

    return (
        <div
            className={classNames(
                className,
                expanded ? 'text-primary-500' : 'text-color-300 hover:text-primary'
            )}>
            <div
                onClick={onClick}
                style={{padding: '4px 10px'}}
                className="flex items-center gap-1 cursor-pointer">
                {prefixIcon}
                {renderTitle(
                    <span className="ml-1 text-doc-sm italic tracking-wide leading-tight">{t(title)}</span>
                )}
                <Component size={12} style={{marginTop: 1}}/>
            </div>
        </div>
    )
}

export const ExpandablePanel = ({
                                    prefixIcon,
                                    icons,
                                    style,
                                    children,
                                    className,
                                    renderTitle,
                                    onClick = NullAsDefault,
                                    expanded = false,
                                    Header = DefaultAdvancedToggle,
                                    Wrapper = renderOwnChild,
                                    title = 'advanced settings',
                                    renderCollapsedContent = NullAsDefault,
                                    ContentWrapper = renderOwnChild,
                                }) => {
    const renderWrapper = useCallback(
        (child) => (
            <Wrapper
                {...{
                    style,
                    onClick,
                    className,
                }}>
                {child}
            </Wrapper>
        ),
        []
    )
    return renderWrapper(
        <div>
            <Header
                {...{
                    title,
                    prefixIcon,
                    icons,
                    onClick,
                    expanded,
                    renderTitle,
                }}
            />
            {!!expanded ? (
                <ContentWrapper>{children}</ContentWrapper>
            ) : (
                renderCollapsedContent(<ContentWrapper>{children}</ContentWrapper>)
            )}
        </div>
    )
}

const AdvancedFormGroup = ({groupValues, Wrapper = renderOwnChild}) => {
    const [isToggle, toggle] = useToggle(false)

    const formSchema = Array.from(groupValues || []).filter(Visible)

    if (_.isEmpty(formSchema)) return null

    return (
        <ExpandablePanel
            onClick={toggle}
            Wrapper={Wrapper}
            expanded={isToggle}>
            <div className="space-y-3">
                <FieldsFactory formSchema={formSchema}/>
            </div>
        </ExpandablePanel>
    )
}

export const AdvancedFormFields = ({
                                       formSchema,
                                       Wrapper = renderOwnChild,
                                       ...props
                                   }) => {
    const [isToggle, toggle] = useToggle(false)

    if (_.isEmpty(formSchema)) return null

    return (
        <ExpandablePanel
            onClick={toggle}
            Wrapper={Wrapper}
            expanded={isToggle}
            {...props}>
            <div className="space-y-3">
                <FieldsFactory formSchema={formSchema}/>
            </div>
        </ExpandablePanel>
    )
}

export const withAdvancedOptions = (options) => (Component) =>
    mapProps(({name, value, onChange, disabled}) => ({
        name,
        value,
        options,
        onChange,
        disabled,
    }))(Component)

export default AdvancedFormGroup
